import React, { useCallback, useEffect, useState } from "react";
import {
  Search,
  User,
  Zap,
  DollarSign,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { callBackend } from "../utils/api";
import { log } from "../utils/logger";

export default function AllPlayers({ onInspect }) {
  const [data, setData] = useState({ players: [], pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("level");
  const [loading, setLoading] = useState(true);

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callBackend(
        `/users/all?page=${page}&search=${search}&sort=${sort}`,
      );
      setData(res);
    } catch (e) {
      log("ALL_PLAYERS", "Failed to fetch players", e);
    } finally {
      setLoading(false);
    }
  }, [page, search, sort]);

  useEffect(() => {
    const delayDebounce = setTimeout(fetchPlayers, 300);
    return () => clearTimeout(delayDebounce);
  }, [fetchPlayers]);

  return (
    <div className="bg-black border-2 border-zinc-800 p-6 shadow-2xl font-mono animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div>
          <h2 className="text-3xl font-black italic uppercase text-white leading-none">
            The Whitepages
          </h2>
          <span className="text-zinc-500 text-[10px] uppercase tracking-widest">
            Largest database of residents in Trap City.
          </span>
        </div>

        <div className="flex flex-1 max-w-md w-full relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            size={16}
          />
          <input
            placeholder="SEARCH THE STREETS..."
            className="w-full bg-zinc-900 border-2 border-zinc-800 p-2 pl-10 text-white text-xs focus:border-green-600 outline-none transition-all"
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            value={search}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "level", label: "Rank" },
          //{ id: "power", label: "Street Power" },
          //{ id: "wealth", label: "Wealth" },
          { id: "newest", label: "Fresh Fish" },
        ].map((btn) => (
          <button
            key={btn.id}
            onClick={() => {
              setSort(btn.id);
              setPage(1);
            }}
            className={`px-3 py-1 text-[10px] font-black uppercase border transition-all ${sort === btn.id ? "bg-green-600 border-green-500 text-black" : "border-zinc-800 text-zinc-500 hover:text-white"}`}
          >
            {btn.label}
          </button>
        ))}
      </div>

      <div className="min-h-[400px]">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-green-500 animate-pulse uppercase text-sm">
            Scanning Blocks...
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.players.map((player) => (
              <div
                key={player.id}
                onClick={() => onInspect(player.id)}
                className="bg-zinc-950 border border-zinc-900 p-4 hover:border-zinc-600 cursor-pointer group transition-all relative overflow-hidden"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden">
                    {player.discordUser?.avatar ? (
                      <img
                        src={player.discordUser.avatar}
                        alt={player.username}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User
                        size={20}
                        className="text-zinc-500 group-hover:text-white"
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {player.is_active && (
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_5px_green]" />
                      )}
                      <span
                        className="font-black text-sm uppercase"
                        style={{ color: player.level_color }}
                      >
                        {player.tag && (
                          <span className="text-blue-500 text-xs italic mr-1">
                            [{player.tag}]
                          </span>
                        )}
                        {player.username}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-[9px] text-zinc-500 flex items-center gap-1 font-bold">
                        <Zap size={10} />{" "}
                        {Math.round(player.street_power).toLocaleString()}
                      </span>
                      <span className="text-[9px] text-zinc-500 flex items-center gap-1 font-bold">
                        <DollarSign size={10} /> $
                        {(player.cash || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-black italic block text-zinc-400">
                      LVL {player.level}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-8 border-t border-zinc-900 pt-4">
        <span className="text-[10px] text-zinc-500 uppercase font-black">
          Total: {data.total} Players
        </span>
        <div className="flex gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="p-2 border border-zinc-800 hover:bg-zinc-900 disabled:opacity-20"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="px-4 py-2 bg-zinc-900 border border-zinc-800 text-[10px] font-black">
            PAGE {page} / {data.pages || 1}
          </div>
          <button
            disabled={page >= data.pages}
            onClick={() => setPage((p) => p + 1)}
            className="p-2 border border-zinc-800 hover:bg-zinc-900 disabled:opacity-20"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
